import React, { useMemo } from 'react';
import type { StageState, StageItemState } from '@skd/shared';
import { INSPECTION_STAGES, INSPECTION_ITEMS, isItemApplicable, DEFECT_TYPES } from '@skd/shared';
import { Button } from '../ui/Button';
import { StatusChip } from '../ui/StatusChip';

interface SectionPageProps {
  stageId: number;
  stageData: StageState;
  itemStates: Record<string, StageItemState>;
  onToggleItem: (itemId: string) => void;
  onSetDefectType: (itemId: string, defectType: string) => void;
  onUpdateRemark: (itemId: string, remark: string) => void;
  onUpdateInspector: (value: string) => void;
  onSave: () => void;
  onPrev: () => void;
  onNext: () => void;
  isLast: boolean;
}

export function SectionPage({
  stageId,
  stageData,
  itemStates,
  onToggleItem,
  onSetDefectType,
  onUpdateRemark,
  onUpdateInspector,
  onSave,
  onPrev,
  onNext,
  isLast,
}: SectionPageProps) {
  const stage = INSPECTION_STAGES.find((s) => s.id === stageId);
  const applicableItems = useMemo(
    () => INSPECTION_ITEMS.filter((item) => isItemApplicable(item.id, stageId)),
    [stageId]
  );

  const ngCount = applicableItems.filter((i) => itemStates[i.id]?.status === 'NG').length;

  return (
    <div className="card">
      {/* Section header */}
      <div className="flex flex-col gap-3 border-b border-[var(--border-color)] p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="min-w-0">
          <h2 className="card-title">{stage?.name || `Section ${stageId}`}</h2>
          <p className="mt-0.5 text-[0.75rem] text-[var(--text-dim)]">
            Section {stageId} of 5 · {applicableItems.length} items ·{' '}
            {ngCount === 0 ? 'no defects' : `${ngCount} flagged`}
          </p>
        </div>
        {stageData.saved ? (
          <StatusChip status="ok">
            Saved by {stageData.savedBy} at {stageData.savedAt}
          </StatusChip>
        ) : (
          <StatusChip status="warning">Not saved yet</StatusChip>
        )}
      </div>

      {/* Item grid */}
      <div className="p-4 sm:p-5">
        <p className="mb-3 text-[0.8125rem] text-[var(--text-muted)]">
          Tap a compartment to toggle between <strong className="font-medium text-[var(--text-main)]">OK</strong> and{' '}
          <strong className="font-medium text-[var(--text-main)]">NG</strong>.
        </p>

        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {applicableItems.map((item) => {
            const itemState = itemStates[item.id];
            if (!itemState) return null;
            const isNG = itemState.status === 'NG';
            const isOK = itemState.status === 'OK';

            return (
              <div
                key={item.id}
                className={`rounded-[var(--radius)] border transition-colors ${
                  isNG
                    ? 'border-[var(--status-ng-border)] bg-[var(--status-ng-bg)]'
                    : isOK
                    ? 'border-[var(--border-color)] bg-[var(--card-bg)]'
                    : 'border-[var(--border-color)] bg-[var(--card-bg-subtle)]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onToggleItem(item.id)}
                  aria-pressed={isNG}
                  className="flex w-full items-start justify-between gap-3 p-3 text-left"
                >
                  <span className="min-w-0">
                    <span className="flex items-baseline gap-1.5">
                      <span className="text-[0.6875rem] text-[var(--text-dim)]">({item.id})</span>
                      <span className="truncate text-[0.875rem] font-medium text-[var(--text-main)]">
                        {item.code}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[0.75rem] text-[var(--text-muted)]">{item.label}</span>
                  </span>
                  <span className="flex-shrink-0">
                    {isNG ? (
                      <StatusChip status="ng">NG</StatusChip>
                    ) : isOK ? (
                      <StatusChip status="ok">OK</StatusChip>
                    ) : (
                      <StatusChip status="neutral">N/A</StatusChip>
                    )}
                  </span>
                </button>

                {isNG && (
                  <div className="space-y-2.5 border-t border-[var(--status-ng-border)] px-3 pb-3 pt-2.5">
                    <div>
                      <span className="mb-1.5 block text-[0.6875rem] font-medium uppercase tracking-wider text-[var(--text-dim)]">
                        Defect type
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {DEFECT_TYPES.map((dt) => (
                          <button
                            key={dt}
                            type="button"
                            onClick={() => onSetDefectType(item.id, dt)}
                            className={`rounded-[var(--radius-sm)] border px-2 py-1 text-[0.75rem] font-medium transition-colors ${
                              itemState.defectType === dt
                                ? 'border-[var(--status-ng-border)] bg-[var(--status-ng-border)] text-white'
                                : 'border-[var(--border-color)] bg-[var(--card-bg)] text-[var(--text-muted)] hover:border-[var(--border-hover)]'
                            }`}
                          >
                            {dt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-[0.6875rem] font-medium uppercase tracking-wider text-[var(--text-dim)]"
                        htmlFor={`remark-${stageId}-${item.id}`}
                      >
                        Remark
                      </label>
                      <input
                        id={`remark-${stageId}-${item.id}`}
                        type="text"
                        className="input-field"
                        placeholder="Add a remark"
                        value={itemState.remark}
                        onChange={(e) => onUpdateRemark(item.id, e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sign-off */}
        <div className="mt-5 grid grid-cols-1 gap-4 rounded-[var(--radius)] border border-[var(--border-color)] bg-[var(--card-bg-subtle)] p-4 sm:grid-cols-2">
          <div className="form-group">
            <label className="form-label" htmlFor={`inspector-${stageId}`}>Inspector name</label>
            <input
              id={`inspector-${stageId}`}
              type="text"
              className="input-field"
              placeholder="Inspector full name"
              value={stageData.inspectorFure}
              onChange={(e) => onUpdateInspector(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor={`datetime-${stageId}`}>Date &amp; time</label>
            <input
              id={`datetime-${stageId}`}
              type="text"
              className="input-field font-mono"
              value={stageData.dateTime}
              readOnly
            />
          </div>
          <div className="form-group sm:col-span-2">
            <span className="form-label">Action status</span>
            <div>
              {stageData.actionStatus === 'SKD Body OK' ? (
                <StatusChip status="ok">{stageData.actionStatus}</StatusChip>
              ) : (
                <StatusChip status="ng">{stageData.actionStatus}</StatusChip>
              )}
            </div>
          </div>
        </div>

        {/* Sticky on phones so the primary actions stay in thumb reach. */}
        <div className="wizard-sticky-actions mt-5 md:justify-between">
          <Button variant="secondary" onClick={onPrev} className="flex-shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
            <span className="hidden sm:inline">Previous</span>
          </Button>
          <div className="flex flex-1 items-center gap-2 md:flex-none">
            <Button variant="secondary" onClick={onSave} className="flex-1 md:flex-none">
              Save section
            </Button>
            <Button variant="primary" onClick={onNext} className="flex-1 md:flex-none">
              {isLast ? 'Finish' : 'Next section'}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
